import React from 'react';
import { createPortal } from 'react-dom';
import { Wrench, Clock, AlertCircle } from 'lucide-react';

interface MaintenanceOverlayProps {
  isVisible: boolean;
  title?: string;
  message?: string;
  estimatedDuration?: string;
}

const MaintenanceOverlay: React.FC<MaintenanceOverlayProps> = ({
  isVisible,
  title = "CLIPTHIS is under maintenance",
  message = "We're making some improvements to serve you better. We'll be back online shortly.",
  estimatedDuration = "We expect to be back online within the next few hours"
}) => {
  if (!isVisible) return null;

  const maintenanceContent = (
    <div 
      className="fixed inset-0 bg-white flex items-center justify-center relative overflow-hidden"
      style={{ 
        zIndex: 99999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh'
      }}
    >
      {/* Dot pattern background - same as other pages */}
      <div className="absolute inset-0 dot-pattern-animated opacity-20 pointer-events-none" />

      <div className="relative z-10 text-center max-w-2xl mx-auto px-6">
        {/* Logo/Brand */}
        <div className="mb-12">
          <h1 className="text-5xl font-display font-bold tracking-tight text-gray-900 mb-4">
            CLIPTHIS
          </h1>
          <div className="w-16 h-0.5 bg-red-600 mx-auto" />
        </div>

        {/* Maintenance Icon */}
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto bg-red-600 rounded-full flex items-center justify-center mb-4">
            <Wrench className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Main message */}
        <div className="mb-8">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {message}
          </p>
        </div>

        {/* Status cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-center mb-3">
              <Clock className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Expected Duration</h3>
            </div>
            <p className="text-gray-600">
              {estimatedDuration}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-center mb-3">
              <AlertCircle className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">What's Happening</h3>
            </div>
            <p className="text-gray-600">
              System upgrades and performance improvements
            </p>
          </div>
        </div>

        {/* Contact info */}
        <div className="bg-red-50 rounded-xl p-6 border border-red-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Need immediate assistance?</h3>
          <p className="text-gray-600 mb-4">
            If you have urgent questions or concerns, please reach out to us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:support@clipthis.app" 
              className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              Contact Support
            </a>
            <a 
              href="https://twitter.com/clipthis" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Follow Updates
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-sm text-gray-500">
          <p>Thank you for your patience while we improve CLIPTHIS</p>
          <p className="mt-2">This page will automatically refresh when we're back online</p>
        </div>
      </div>
    </div>
  );

  // Render at document root level to ensure full-screen coverage
  return createPortal(maintenanceContent, document.body);
};

export default MaintenanceOverlay;
